/**
 * Women's Health Intake Template
 * 
 * HRT/Hormone Replacement Therapy and women's wellness protocols
 * Comprehensive symptom assessment and medical history
 */

import { IntakeTemplate, IntakeSection, IntakeQuestion } from './types';

// ============================================================================
// Symptom Categories - Matching Peptide Ledger Structure
// ============================================================================

const VASOMOTOR_SYMPTOMS: IntakeQuestion[] = [
  {
    id: 'symptom_hot_flashes',
    text: 'Hot flashes',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'vasomotor'
  },
  {
    id: 'symptom_night_sweats',
    text: 'Night sweats',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'vasomotor'
  },
  {
    id: 'symptom_cold_intolerance',
    text: 'Cold intolerance',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'vasomotor'
  }
];

const MOOD_COGNITIVE_SYMPTOMS: IntakeQuestion[] = [
  {
    id: 'symptom_mood_swings',
    text: 'Mood swings',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'mood'
  },
  {
    id: 'symptom_anxiety',
    text: 'Anxiety',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'mood'
  },
  {
    id: 'symptom_depression',
    text: 'Depression',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'mood'
  },
  {
    id: 'symptom_brain_fog',
    text: 'Brain fog / Memory issues',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'mood'
  },
  {
    id: 'symptom_irritability',
    text: 'Irritability',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'mood'
  }
];

const PHYSICAL_SYMPTOMS: IntakeQuestion[] = [
  {
    id: 'symptom_fatigue',
    text: 'Fatigue / Low energy',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'physical'
  },
  {
    id: 'symptom_joint_pain',
    text: 'Joint pain',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'physical'
  },
  {
    id: 'symptom_muscle_aches',
    text: 'Muscle aches',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'physical'
  },
  {
    id: 'symptom_weight_gain',
    text: 'Weight gain / Difficulty losing weight',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'physical'
  },
  {
    id: 'symptom_breast_tenderness',
    text: 'Breast tenderness',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'physical'
  }
];

const REPRODUCTIVE_SYMPTOMS: IntakeQuestion[] = [
  {
    id: 'symptom_irregular_cycles',
    text: 'Irregular menstrual cycles',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'reproductive'
  },
  {
    id: 'symptom_heavy_bleeding',
    text: 'Heavy menstrual bleeding',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'reproductive'
  },
  {
    id: 'symptom_decreased_libido',
    text: 'Decreased libido',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'reproductive'
  },
  {
    id: 'symptom_vaginal_dryness',
    text: 'Vaginal dryness',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'reproductive'
  },
  {
    id: 'symptom_painful_intercourse',
    text: 'Painful intercourse',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'reproductive'
  }
];

const SLEEP_SYMPTOMS: IntakeQuestion[] = [
  {
    id: 'symptom_insomnia',
    text: 'Difficulty falling asleep',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    required: true,
    category: 'sleep'
  },
  {
    id: 'symptom_frequent_waking',
    text: 'Frequent waking during night',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'sleep'
  },
  {
    id: 'symptom_unrefreshing_sleep',
    text: 'Unrefreshing sleep',
    inputType: 'scale',
    options: ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
    category: 'sleep'
  }
];

// ============================================================================
// Sections
// ============================================================================

const MENOPAUSAL_STATUS_SECTION: IntakeSection = {
  id: 'menopausal_status',
  title: 'Menopausal Status',
  description: 'Understanding your hormonal stage helps determine the right treatment approach.',
  icon: 'heart',
  questions: [
    {
      id: 'menopausal_status',
      text: 'What is your current menopausal status?',
      inputType: 'select',
      options: [
        'Pre-menopausal (regular periods)',
        'Peri-menopausal (irregular periods, some symptoms)',
        'Post-menopausal (no period for 12+ months)',
        'Surgical menopause (hysterectomy/oophorectomy)',
        'Unsure'
      ],
      required: true
    },
    {
      id: 'last_period_date',
      text: 'When was your last menstrual period?',
      inputType: 'text',
      helpText: 'Approximate date or "N/A" if surgical menopause'
    },
    {
      id: 'hysterectomy',
      text: 'Have you had a hysterectomy?',
      inputType: 'boolean',
      required: true,
      helpText: 'Important for determining progesterone needs'
    },
    {
      id: 'ovaries_removed',
      text: 'If you had a hysterectomy, were your ovaries removed?',
      inputType: 'select',
      options: ['Yes - both ovaries', 'Yes - one ovary', 'No - ovaries intact', 'N/A'],
      conditionalOn: {
        questionId: 'hysterectomy',
        operator: 'equals',
        value: true
      }
    },
    {
      id: 'birth_control_current',
      text: 'Are you currently using hormonal birth control?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'pregnancies_count',
      text: 'How many pregnancies have you had?',
      inputType: 'select',
      options: ['0', '1', '2', '3', '4+']
    }
  ]
};

const SYMPTOMS_SECTION: IntakeSection = {
  id: 'symptoms',
  title: 'Current Symptoms',
  description: 'Rate the severity of any symptoms you are experiencing (0 = None, 4 = Very Severe)',
  icon: 'thermometer',
  questions: [
    ...VASOMOTOR_SYMPTOMS,
    ...MOOD_COGNITIVE_SYMPTOMS,
    ...PHYSICAL_SYMPTOMS,
    ...REPRODUCTIVE_SYMPTOMS,
    ...SLEEP_SYMPTOMS,
    {
      id: 'symptom_additional_notes',
      text: 'Additional symptoms or notes:',
      inputType: 'text'
    }
  ]
};

const MEDICAL_HISTORY_SECTION: IntakeSection = {
  id: 'medical_history',
  title: 'Medical History',
  description: 'Important safety information for hormone therapy.',
  icon: 'clipboard',
  questions: [
    {
      id: 'breast_cancer_personal',
      text: 'Have you ever been diagnosed with breast cancer?',
      inputType: 'boolean',
      required: true,
      helpText: 'Critical contraindication for certain hormone therapies'
    },
    {
      id: 'breast_cancer_family',
      text: 'Do you have a family history of breast cancer (mother, sister, daughter)?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'uterine_cancer',
      text: 'Have you ever been diagnosed with uterine/endometrial cancer?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'blood_clots',
      text: 'Have you ever had blood clots (DVT, PE, stroke)?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'cardiovascular_disease',
      text: 'Do you have any heart disease or history of heart attack?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'liver_disease',
      text: 'Do you have any liver disease?',
      inputType: 'boolean',
      required: true
    },
    {
      id: 'migraine_with_aura',
      text: 'Do you experience migraines with aura?',
      inputType: 'boolean',
      required: true,
      helpText: 'May affect estrogen therapy options'
    },
    {
      id: 'thyroid_condition',
      text: 'Do you have any thyroid conditions?',
      inputType: 'select',
      options: ['No', 'Hypothyroid (low)', 'Hyperthyroid (high)', 'Hashimotos', 'Other'],
      required: true
    },
    {
      id: 'previous_hrt',
      text: 'Have you used hormone therapy before?',
      inputType: 'select',
      options: ['Never', 'Previous (stopped)', 'Currently using'],
      required: true
    },
    {
      id: 'previous_hrt_details',
      text: 'If previous/current HRT, please describe:',
      inputType: 'text',
      conditionalOn: {
        questionId: 'previous_hrt',
        operator: 'notEquals',
        value: 'Never'
      }
    },
    {
      id: 'current_medications',
      text: 'Select all current medications and supplements:',
      inputType: 'multiselect',
      options: [
        'Levothyroxine', 'Liothyronine', 'Armour Thyroid',
        'Lisinopril', 'Amlodipine', 'Losartan', 'Metoprolol', 'Hydrochlorothiazide',
        'Metformin', 'Insulin', 'Glipizide', 'Empagliflozin',
        'Atorvastatin', 'Rosuvastatin', 'Simvastatin',
        'Sertraline', 'Fluoxetine', 'Escitalopram', 'Bupropion', 'Trazodone',
        'Estradiol', 'Progesterone', 'Oral contraceptives', 'Testosterone', 'DHEA',
        'Ibuprofen', 'Naproxen', 'Acetaminophen', 'Gabapentin', 'Prednisone',
        'Omeprazole', 'Pantoprazole', 'Montelukast', 'Albuterol',
        'Vitamin D', 'Vitamin B12', 'Iron supplement', 'Calcium', 'Magnesium', 'Multivitamin', 'Fish oil / Omega-3', 'Probiotics',
      ],
      required: true,
      helpText: 'Select all that apply. You can also list additional medications not shown here.'
    },
    {
      id: 'allergies',
      text: 'Do you have any drug allergies?',
      inputType: 'text',
      required: true
    }
  ]
};

const LIFESTYLE_SECTION: IntakeSection = {
  id: 'lifestyle',
  title: 'Lifestyle',
  description: 'Information about your daily habits.',
  icon: 'activity',
  questions: [
    {
      id: 'activity_level',
      text: 'How would you describe your activity level?',
      inputType: 'select',
      options: ['Sedentary', 'Lightly Active', 'Moderately Active', 'Very Active'],
      required: true
    },
    {
      id: 'sleep_quality',
      text: 'How would you rate your overall sleep quality?',
      inputType: 'scale',
      options: ['Very Poor', 'Poor', 'Fair', 'Good', 'Excellent'],
      required: true
    },
    {
      id: 'sleep_hours',
      text: 'How many hours of sleep do you typically get?',
      inputType: 'select',
      options: ['Less than 5', '5-6', '6-7', '7-8', '8+'],
      required: true
    },
    {
      id: 'stress_level',
      text: 'How would you rate your current stress level?',
      inputType: 'scale',
      options: ['Very Low', 'Low', 'Moderate', 'High', 'Very High'],
      required: true
    },
    {
      id: 'alcohol_use',
      text: 'How many alcoholic drinks do you consume per week?',
      inputType: 'select',
      options: ['None', '1-3', '4-7', '8-14', '15+']
    },
    {
      id: 'smoking_status',
      text: 'Do you smoke?',
      inputType: 'select',
      options: ['Never', 'Former smoker', 'Current smoker'],
      required: true
    }
  ]
};

const SHIPPING_BILLING_SECTION: IntakeSection = {
  id: 'shipping_billing_address',
  title: 'Shipping & Billing Address',
  description: 'Where should we ship your medications and send billing?',
  icon: 'package',
  questions: [
    {
      id: 'shipping_address_line1',
      text: 'Shipping Street Address',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_address_line2',
      text: 'Shipping Apt / Suite / Unit',
      inputType: 'text',
    },
    {
      id: 'shipping_city',
      text: 'Shipping City',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_state',
      text: 'Shipping State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      required: true,
    },
    {
      id: 'shipping_postal_code',
      text: 'Shipping ZIP Code',
      inputType: 'text',
      required: true,
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
    {
      id: 'billing_same_as_shipping',
      text: 'Is your billing address the same as your shipping address?',
      inputType: 'boolean',
      required: true,
    },
    {
      id: 'billing_address_line1',
      text: 'Billing Street Address',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_address_line2',
      text: 'Billing Apt / Suite / Unit',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_city',
      text: 'Billing City',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_state',
      text: 'Billing State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_postal_code',
      text: 'Billing ZIP Code',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
  ]
};

const GOALS_SECTION: IntakeSection = {
  id: 'goals',
  title: 'Treatment Goals',
  description: 'What would you like to address with treatment?',
  icon: 'target',
  questions: [
    {
      id: 'treatment_goals',
      text: 'What are your primary goals? (Select all that apply)',
      inputType: 'multiselect',
      options: [
        'Relief from hot flashes/night sweats',
        'Improve sleep quality',
        'Stabilize mood',
        'Increase energy',
        'Improve libido',
        'Address vaginal dryness',
        'Better cognitive function',
        'Weight management',
        'Overall hormone optimization'
      ],
      required: true
    },
    {
      id: 'treatment_preferences',
      text: 'Do you have any preferences for treatment type?',
      inputType: 'multiselect',
      options: [
        'Bioidentical hormones only',
        'Topical/transdermal (creams, patches)',
        'Oral medications',
        'Prefer natural/non-hormonal options',
        'Open to provider recommendation'
      ]
    },
    {
      id: 'additional_questions',
      text: 'Any additional questions or concerns for your provider?',
      inputType: 'text'
    }
  ]
};

// ============================================================================
// Complete Template
// ============================================================================

export const WOMENS_HEALTH_TEMPLATE: IntakeTemplate = {
  id: 'womens_health_intake',
  templateType: 'womens_health',
  name: "Women's Health Intake (HRT, Hormone Balance)",
  description: 'Comprehensive intake for hormone replacement therapy and women\'s wellness. Includes detailed symptom assessment and medical history for safe, personalized treatment.',
  estimatedTime: '10-15 minutes',
  sections: [
    MENOPAUSAL_STATUS_SECTION,
    SYMPTOMS_SECTION,
    MEDICAL_HISTORY_SECTION,
    LIFESTYLE_SECTION,
    SHIPPING_BILLING_SECTION,
    GOALS_SECTION
  ],
  scoringLogic: {
    type: 'sum',
    questionsToScore: [
      ...VASOMOTOR_SYMPTOMS,
      ...MOOD_COGNITIVE_SYMPTOMS,
      ...PHYSICAL_SYMPTOMS,
      ...REPRODUCTIVE_SYMPTOMS,
      ...SLEEP_SYMPTOMS
    ].map(q => q.id),
    thresholds: {
      low: 10,
      medium: 25,
      high: 40
    },
    interpretation: {
      low: 'Mild symptoms - may benefit from lifestyle modifications or mild intervention',
      medium: 'Moderate symptoms - hormone therapy likely beneficial',
      high: 'Significant symptoms - comprehensive hormone evaluation recommended'
    }
  }
};

/**
 * Calculate total symptom severity score
 */
export function calculateSymptomScore(responses: Record<string, number>): {
  totalScore: number;
  categoryScores: Record<string, number>;
  severity: 'mild' | 'moderate' | 'significant';
  interpretation: string;
} {
  const categories = {
    vasomotor: VASOMOTOR_SYMPTOMS.map(q => q.id),
    mood: MOOD_COGNITIVE_SYMPTOMS.map(q => q.id),
    physical: PHYSICAL_SYMPTOMS.map(q => q.id),
    reproductive: REPRODUCTIVE_SYMPTOMS.map(q => q.id),
    sleep: SLEEP_SYMPTOMS.map(q => q.id)
  };

  const categoryScores: Record<string, number> = {};
  let totalScore = 0;

  for (const [category, questionIds] of Object.entries(categories)) {
    const categoryTotal = questionIds.reduce((sum, id) => sum + (responses[id] || 0), 0);
    categoryScores[category] = categoryTotal;
    totalScore += categoryTotal;
  }

  let severity: 'mild' | 'moderate' | 'significant';
  let interpretation: string;

  if (totalScore <= 10) {
    severity = 'mild';
    interpretation = 'Mild symptoms - may benefit from lifestyle modifications';
  } else if (totalScore <= 25) {
    severity = 'moderate';
    interpretation = 'Moderate symptoms - hormone therapy likely beneficial';
  } else {
    severity = 'significant';
    interpretation = 'Significant symptoms - comprehensive hormone evaluation recommended';
  }

  return {
    totalScore,
    categoryScores,
    severity,
    interpretation
  };
}

export default WOMENS_HEALTH_TEMPLATE;
